import React, { useMemo } from 'react';
import { useTranslate } from '@im/react-localization';
import { GridColumns, GridValidRowModel } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Table, { TableProps } from 'src/components/Table';
import { useRoles } from 'src/provider/UserRoleProvider';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

export type { GridSelectionModel } from '@mui/x-data-grid';

interface ReusableTableProps<T extends GridValidRowModel>
  extends Omit<TableProps<T>, 'columns'> {
  isProductGroups: boolean;
  removeItem: (resource: T) => void;
}

const ReusableTable = <T extends GridValidRowModel>({
  isProductGroups,
  removeItem,
  ...props
}: ReusableTableProps<T>) => {
  const translate = useTranslate();
  const { isCOGUser } = useRoles();
  const formatDateTime = useDateTimeFormatter();

  const columns = useMemo<GridColumns>(
    (): GridColumns<T> => [
      {
        field: isProductGroups ? 'name' : 'customerName',
        flex: 1,
        headerName: translate(
          isProductGroups ? 'ProductGroups.Name' : 'CustomerGroups.customerName'
        ),
        minWidth: 140,
        sortable: false,
        valueGetter: ({ row }) =>
          isProductGroups ? row.name : row.customerName,
      },
      {
        field: isProductGroups ? 'globalPlanId' : 'customerNumber',
        flex: 0.75,
        headerName: translate(
          isProductGroups
            ? 'ProductGroups.GlobalPlanId'
            : 'CustomerGroups.customerNumber'
        ),
        minWidth: 100,
        sortable: false,
        valueGetter: ({ row }) =>
          isProductGroups ? row.globalPlanId : row.customerNumber,
      },
      {
        field: isProductGroups ? 'globalSku' : 'masterCustomerName',
        flex: 1,
        headerName: translate(
          isProductGroups
            ? 'ProductGroups.GlobalSKU'
            : 'CustomerGroups.masterCustomerName'
        ),
        minWidth: 140,
        sortable: false,
        valueGetter: ({ row }) =>
          isProductGroups ? row.globalSKU : row.masterCustomerName,
      },
      ...(isProductGroups
        ? []
        : [
            {
              field: 'masterCustomerNumber',
              flex: 0.75,
              headerName: translate('CustomerGroups.masterCustomerNumber'),
              minWidth: 100,
              sortable: false,
              valueGetter: ({ row }: any) => row.masterCustomerNumber,
            },
          ]),
      {
        field: 'updated',
        flex: 0.75,
        headerName: translate('CustomerGroups.updated'),
        minWidth: 100,
        sortable: false,
        valueGetter: ({ row }) =>
          formatDateTime(isProductGroups ? row.lastUpdated : row.modified),
      },
      {
        field: 'updatedBy',
        flex: 0.75,
        headerName: translate('CustomerGroups.updatedBy'),
        minWidth: 100,
        sortable: false,
        valueGetter: ({ row }) =>
          isProductGroups ? row.updatedBy : row.modifiedBy,
      },
      {
        align: 'right',
        field: '',
        renderCell: ({ row }) => (
          <Button
            onClick={() => removeItem(row)}
            sx={(theme) => ({
              display: isCOGUser ? 'inherit' : 'none',
              minWidth: 0,
              padding: theme.spacing(0.75),
            })}
            title={translate(
              isProductGroups
                ? 'ProductGroups.RemoveProducts'
                : 'CustomerGroups.removeCustomer'
            )}
          >
            <DeleteOutlinedIcon
              fontSize="small"
              sx={(theme) => ({
                color: `${theme.palette.grey[600]} !important`,
              })}
            />
          </Button>
        ),
        sortable: false,
      },
    ],
    [isProductGroups, translate, formatDateTime, removeItem, isCOGUser]
  );

  const tableProps = {
    columns,
    ...props,
  };

  return (
    <Box my={2} position="relative">
      <Table<T> {...tableProps} />
    </Box>
  );
};

export default ReusableTable;
